import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { BgVideo } from "./LiquidGlass";

// Liminal environment section: a full-bleed space you walk THROUGH — the
// backdrop dollies forward (scale scrub) and drifts like a slow Ken Burns
// shot while content floats above on HUD panels.
// env: "station" | "forest" | "cyber" — or pass video="/videos/x.mp4" for a live environment.
export const LiminalSection = ({ env, video, children, className = "", testid, strength = 0.16, calm = false }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(p, [0, 1], [1 + strength, 1]);
  const y = useTransform(p, [0, 1], ["-4%", "4%"]);

  return (
    <section ref={ref} data-testid={testid} className={`relative overflow-hidden ${className}`}>
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 will-change-transform"
        style={reduce ? undefined : { scale, y }}
      >
        {video ? (
          <>
            <img
              src={`/env/${env}.jpg`}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover opacity-40"
            />
            <BgVideo src={video} className="!opacity-50" />
          </>
        ) : (
          <motion.img
            src={`/env/${env}.jpg`}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            animate={reduce || calm ? {} : { x: [0, -18, 0], rotate: [0, 0.4, 0] }}
            transition={{ duration: 34, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
      </motion.div>
      {/* readability + mood overlays */}
      <div aria-hidden="true" className="absolute inset-0 bg-espresso/72" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-paper via-transparent to-paper" />
      <div aria-hidden="true" className="scanlines absolute inset-0 opacity-[0.05]" />
      {/* neon grid floor */}
      <div aria-hidden="true" className="neon-floor absolute inset-x-0 bottom-0 h-40" />
      <div className="relative z-10">{children}</div>
    </section>
  );
};

// Iron-Man-style HUD frame: corner brackets, side readouts, rotating reticle.
export const HudFrame = ({ label, coords, children, className = "", reticle = true }) => {
  const reduce = useReducedMotion();
  return (
    <div className={`relative ${className}`}>
      <span aria-hidden="true" className="gf-corner left-0 top-0 border-l border-t border-volt/50" />
      <span aria-hidden="true" className="gf-corner right-0 top-0 border-r border-t border-volt/50" />
      <span aria-hidden="true" className="gf-corner bottom-0 left-0 border-b border-l border-volt/50" />
      <span aria-hidden="true" className="gf-corner bottom-0 right-0 border-b border-r border-volt/50" />
      {label && (
        <span className="absolute -top-3 left-4 z-10 bg-paper px-2 font-mono text-[9px] uppercase tracking-[0.3em] text-volt/90">
          {label}
        </span>
      )}
      {coords && (
        <span className="absolute -bottom-2.5 right-4 z-10 bg-paper px-2 font-mono text-[8px] uppercase tracking-[0.24em] text-ink/40">
          {coords}
        </span>
      )}
      {reticle && !reduce && (
        <motion.span
          aria-hidden="true"
          className="absolute -right-5 -top-5 h-10 w-10 rounded-full border border-dashed border-cyan/50"
          animate={{ rotate: 360 }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
      )}
      {children}
    </div>
  );
};
